<template>
  <div class="animated fadeIn">

    <!-- Loading skeleton -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width:3rem;height:3rem" />
      <div class="mt-2 text-muted">Loading issue…</div>
    </div>

    <!-- Not Found -->
    <b-alert v-else-if="!issue" show variant="warning">
      <i class="icon-exclamation mr-1"></i>Issue not found.
      <b-button size="sm" variant="warning" class="ml-3" to="/issue-tracker/list">Back to List</b-button>
    </b-alert>

    <template v-else>
      <!-- Header -->
      <b-row class="mb-3 align-items-start">
        <b-col>
          <div class="d-flex align-items-center flex-wrap mb-1">
            <span class="text-muted mr-2" style="font-size:.85rem">#{{ issue.id }}</span>
            <b-badge :variant="priorityVariant(issue.priority)" class="mr-1 badge-pill">{{ issue.priority }}</b-badge>
            <b-badge :variant="statusVariant(issue.status)" class="mr-1 badge-pill">{{ issue.status }}</b-badge>
            <b-badge variant="light" class="text-dark border mr-1">{{ issue.client_type }}</b-badge>
          </div>
          <h4 class="mb-0 font-weight-bold">{{ issue.title }}</h4>
          <small class="text-muted">
            Reported by <strong>{{ issue.reported_by }}</strong> · {{ issue.created_at | fmtdt }}
          </small>
        </b-col>
        <b-col class="text-right flex-shrink-1 mt-1">
          <b-button size="sm" variant="outline-secondary" to="/issue-tracker/list">
            <i class="icon-arrow-left mr-1"></i>Back
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <!-- Left: Details + Comments -->
        <b-col md="8" class="mb-4">

          <!-- Issue Info Card -->
          <b-card class="shadow-sm mb-3">
            <b-row>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Client</div>
                <div>
                  <b-badge variant="light" class="text-dark border mr-1">{{ issue.client_type }}</b-badge>
                  <span>{{ issue.client_name || '—' }}</span>
                  <small v-if="issue.client_id" class="text-muted ml-1">({{ issue.client_id }})</small>
                </div>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Category</div>
                <div>{{ issue.category }}</div>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Issue Type</div>
                <div>
                  <b-badge :variant="issue.issue_type === 'DEVICE' ? 'warning' : 'light'"
                           class="text-dark border mr-1">{{ issue.issue_type || 'HRM' }}</b-badge>
                  <span v-if="issue.device_identifier">
                    <i class="fa fa-microchip mx-1 text-muted"></i>{{ issue.device_identifier }}
                    <small v-if="issue.device_id" class="text-muted">(#{{ issue.device_id }})</small>
                  </span>
                </div>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Assigned To</div>
                <div v-if="issue.assigned_name" class="d-flex align-items-center">
                  <span class="avatar-sm mr-2">{{ issue.assigned_name.charAt(0) }}</span>
                  <div>
                    <strong>{{ issue.assigned_name }}</strong>
                    <div class="text-muted" style="font-size:.75rem">{{ issue.assigned_to }}</div>
                  </div>
                </div>
                <span v-else class="text-muted">Unassigned</span>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Due Date</div>
                <div :class="isOverdue ? 'text-danger font-weight-bold' : ''">
                  {{ issue.due_date ? (issue.due_date | fmtdt) : '—' }}
                  <b-badge v-if="isOverdue" variant="danger" class="ml-1" style="font-size:.65rem">OVERDUE</b-badge>
                </div>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Created</div>
                <div>{{ issue.created_at | fmtdt }}</div>
              </b-col>
              <b-col sm="6" class="mb-2">
                <div class="detail-label">Last Updated</div>
                <div>{{ issue.updated_at | fmtdt }}</div>
              </b-col>
              <b-col v-if="issue.resolved_at" sm="6">
                <div class="detail-label">Resolved At</div>
                <div class="text-success">{{ issue.resolved_at | fmtdt }}</div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Description -->
          <b-card class="shadow-sm mb-3" v-if="issue.description">
            <template #header><i class="icon-docs mr-1"></i>Description</template>
            <p class="mb-0" style="white-space:pre-wrap;line-height:1.7">{{ issue.description }}</p>
          </b-card>

          <!-- Comments Timeline -->
          <b-card no-body class="shadow-sm">
            <b-card-header>
              <i class="fa fa-comments mr-1 text-info"></i>
              Comments
              <b-badge variant="info" class="ml-1">{{ comments.length }}</b-badge>
            </b-card-header>
            <b-card-body>

              <!-- Existing comments -->
              <div v-if="loadingComments" class="text-center py-3">
                <b-spinner small variant="info" />
              </div>
              <div v-else-if="comments.length === 0" class="text-muted small text-center py-2">
                No comments yet. Be the first to add an update.
              </div>
              <div v-for="c in comments" :key="c.id" class="comment-item mb-3">
                <div class="d-flex align-items-start">
                  <span class="avatar-sm mr-3 flex-shrink-0">
                    {{ c.commenter_name ? c.commenter_name.charAt(0) : c.commented_by.charAt(0) }}
                  </span>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <strong style="font-size:.9rem">{{ c.commenter_name || c.commented_by }}</strong>
                      <small class="text-muted">{{ c.created_at | fmtdt }}</small>
                    </div>
                    <div class="comment-body">{{ c.comment }}</div>
                  </div>
                </div>
              </div>

              <hr v-if="comments.length > 0" class="my-3" />

              <!-- Add comment -->
              <div>
                <label class="detail-label mb-1">Add Comment</label>
                <b-form-textarea
                  v-model="newComment"
                  rows="3"
                  placeholder="Add an update, note, or next steps…"
                  class="mb-2"
                />
                <b-button variant="info" size="sm" :disabled="postingComment || !newComment.trim()" @click="postComment">
                  <b-spinner v-if="postingComment" small class="mr-1" />
                  {{ postingComment ? 'Posting…' : 'Post Comment' }}
                </b-button>
              </div>

            </b-card-body>
          </b-card>
        </b-col>

        <!-- Right Sidebar -->
        <b-col md="4" class="mb-4">

          <!-- Status Update -->
          <b-card class="shadow-sm mb-3">
            <template #header><i class="icon-settings mr-1"></i>Update Status</template>
            <div class="mb-2">
              <small class="detail-label">Current Status</small>
              <div class="mt-1">
                <b-badge :variant="statusVariant(issue.status)" style="font-size:.85rem;padding:.4em .8em">
                  {{ issue.status }}
                </b-badge>
              </div>
            </div>
            <b-form-group label="New Status" class="mb-2">
              <b-form-select v-model="newStatus" :options="statusOptions" size="sm" />
            </b-form-group>
            <b-button variant="primary" size="sm" block :disabled="updatingStatus || newStatus === issue.status" @click="updateStatus">
              <b-spinner v-if="updatingStatus" small class="mr-1" />
              {{ updatingStatus ? 'Updating…' : 'Update Status' }}
            </b-button>
          </b-card>

          <!-- Assign Member -->
          <b-card class="shadow-sm mb-3">
            <template #header><i class="fa fa-user-plus mr-1 text-success"></i>Assign Member</template>
            <div v-if="issue.assigned_name" class="mb-2 d-flex align-items-center">
              <span class="avatar-sm mr-2">{{ issue.assigned_name.charAt(0) }}</span>
              <div>
                <strong>{{ issue.assigned_name }}</strong>
                <div class="text-muted" style="font-size:.75rem">currently assigned</div>
              </div>
            </div>
            <b-form-group label="Select Member" class="mb-2">
              <b-form-select v-model="selectedMemberId" size="sm">
                <template #first>
                  <b-form-select-option :value="null" disabled>— Choose member —</b-form-select-option>
                </template>
                <b-form-select-option v-for="m in activeMembers" :key="m.id" :value="m.id">
                  {{ m.full_name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
            <b-button variant="success" size="sm" block :disabled="assigning || !selectedMemberId" @click="assignMember">
              <b-spinner v-if="assigning" small class="mr-1" />
              {{ assigning ? 'Assigning…' : 'Assign' }}
            </b-button>
          </b-card>

          <!-- Quick Info -->
          <b-card class="shadow-sm bg-light border-0">
            <div class="d-flex justify-content-between mb-1">
              <small class="text-muted">Total Comments</small>
              <b-badge variant="info">{{ comments.length }}</b-badge>
            </div>
            <div class="d-flex justify-content-between">
              <small class="text-muted">Issue Age</small>
              <small class="font-weight-bold">{{ issueAge }}</small>
            </div>
          </b-card>

        </b-col>
      </b-row>
    </template>

  </div>
</template>

<script>
import issueService from '@/services/issueService'
import teamMemberService from '@/services/teamMemberService'

export default {
  name: 'IssueDetail',
  filters: {
    fmtdt: function(v) { return v ? new Date(v).toLocaleString() : '—' }
  },
  data: function() {
    return {
      loading: true,
      issue: null,
      comments: [],
      loadingComments: false,
      activeMembers: [],
      newComment: '',
      postingComment: false,
      newStatus: '',
      updatingStatus: false,
      selectedMemberId: null,
      assigning: false,
      statusOptions: [
        { value: 'OPEN',        text: 'Open' },
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'PENDING',     text: 'Pending' },
        { value: 'RESOLVED',    text: 'Resolved' },
        { value: 'CLOSED',      text: 'Closed' },
        { value: 'REOPENED',    text: 'Reopened' },
      ]
    }
  },
  computed: {
    isOverdue: function() {
      return this.issue && this.issue.due_date
        && new Date(this.issue.due_date) < new Date()
        && this.issue.status !== 'RESOLVED'
        && this.issue.status !== 'CLOSED'
    },
    issueAge: function() {
      if (!this.issue || !this.issue.created_at) return '—'
      var ms = Date.now() - new Date(this.issue.created_at).getTime()
      var days = Math.floor(ms / 86400000)
      if (days > 0) return days + (days === 1 ? ' day' : ' days')
      var hrs = Math.floor(ms / 3600000)
      return hrs + (hrs === 1 ? ' hour' : ' hours')
    }
  },
  created: function() {
    var id = this.$route.params.id
    this.loadIssue(id)
    this.loadComments(id)
    this.loadActiveMembers()
  },
  methods: {
    loadIssue: function(id) {
      var self = this
      self.loading = true
      issueService.getById(id,
        function(data) {
          self.issue     = data
          self.newStatus = data.status
          self.loading   = false
        },
        function(err) {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    loadComments: function(id) {
      var self = this
      self.loadingComments = true
      issueService.getComments(id,
        function(data) { self.comments = data || []; self.loadingComments = false },
        function()     { self.loadingComments = false }
      )
    },
    loadActiveMembers: function() {
      var self = this
      teamMemberService.listActive(
        function(data) { self.activeMembers = data || [] },
        function() {}
      )
    },
    updateStatus: function() {
      var self = this
      self.updatingStatus = true
      issueService.updateStatus(self.issue.id, { status: self.newStatus },
        function() {
          self.issue.status = self.newStatus
          if (self.newStatus === 'RESOLVED') self.issue.resolved_at = new Date().toISOString()
          self.updatingStatus = false
          self.$bvToast.toast('Status updated to ' + self.newStatus, { title: 'Updated', variant: 'success', solid: true })
        },
        function(err) {
          self.updatingStatus = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    assignMember: function() {
      var self = this
      self.assigning = true
      issueService.assign(self.issue.id, { memberId: self.selectedMemberId },
        function() {
          self.assigning = false
          // Reload issue to get updated assigned_name
          self.loadIssue(self.issue.id)
          self.selectedMemberId = null
          self.$bvToast.toast('Issue assigned successfully.', { title: 'Assigned', variant: 'success', solid: true })
        },
        function(err) {
          self.assigning = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    postComment: function() {
      if (!this.newComment.trim()) return
      var self = this
      var id = self.issue.id
      self.postingComment = true
      issueService.addComment(id, { comment: self.newComment.trim() },
        function() {
          self.newComment     = ''
          self.postingComment = false
          self.loadComments(id)
        },
        function(err) {
          self.postingComment = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    priorityVariant: function(p) {
      var map = { CRITICAL: 'danger', HIGH: 'warning', MEDIUM: 'info', LOW: 'secondary' }
      return map[p] || 'secondary'
    },
    statusVariant: function(s) {
      var map = { OPEN: 'primary', IN_PROGRESS: 'warning', PENDING: 'info',
                  RESOLVED: 'success', CLOSED: 'secondary', REOPENED: 'danger' }
      return map[s] || 'secondary'
    }
  }
}
</script>

<style scoped>
.detail-label { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #6c757d; }
.avatar-sm { width: 36px; height: 36px; border-radius: 50%; background: #20a8d8; color: #fff;
             display: inline-flex; align-items: center; justify-content: center;
             font-size: .9rem; font-weight: 700; flex-shrink: 0; }
.comment-item { padding: 10px 12px; background: #f8f9fa; border-radius: 8px; border-left: 3px solid #20a8d8; }
.comment-body { font-size: .9rem; white-space: pre-wrap; line-height: 1.6; color: #343a40; }
</style>
